/**
 * Purpose: End-of-game Report Card screen with mode-specific rankings, per-player
 *          grades, GPA, and honor titles.
 * Owns: Winner announcement, final standings, report card display for each player,
 *       game-mode-specific ranking logic, rematch/reset buttons.
 * Notes: Speed Run ranks by finish order (score). Honor Roll ranks strictly by GPA
 *        and assigns Valedictorian/Salutatorian titles in multiplayer.
 *        Does not implement grading logic — uses existing engine outputs (CS-17).
 */

import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import {
  getPlayersByGPA,
  getPlayersByFinishOrder,
  getHonorTitle,
  calculateFinalGPA,
} from '../engine/scoring';
import { ReportCard } from '../components/game/ReportCard';
import type { Player } from '../types/game';

// _______________________________________________
//              RANKING HELPERS
// _______________________________________________

/** Ranking label for Honor Roll multiplayer (Valedictorian, Salutatorian). */
function getHonorRollRankingLabel(rank: number, playerCount: number): string | undefined {
  if (playerCount < 2) return undefined;
  if (rank === 0) return 'Valedictorian';
  if (rank === 1) return 'Salutatorian';
  return undefined;
}

/** Get the winner display name and subtitle based on game mode.
 *  Race (Speed Run) = first player to finish wins.
 *  Honor Roll / Pure Classroom = highest GPA wins. */
function getWinnerDisplay(
  players: Player[],
  gameMode: 'speedRun' | 'honorRoll' | 'pureClassroom',
): { winner: Player | null; subtitle: string } {
  if (gameMode === 'speedRun') {
    /* Race mode: winner is strictly the first player to finish — GPA is irrelevant */
    const finishedPlayer = players.find((p) => p.finished) ?? null;
    return { winner: finishedPlayer, subtitle: 'Finished First!' };
  }

  /* Honor Roll and Pure Classroom: highest GPA wins */
  const ranked = getPlayersByGPA(players);
  const winner = ranked[0] ?? null;
  const isSolo = players.length === 1;

  if (isSolo && winner) {
    const title = getHonorTitle(winner.gpa);
    return { winner, subtitle: title };
  }

  return { winner, subtitle: 'Valedictorian' };
}

// _______________________________________________
//              PAGE COMPONENT
// _______________________________________________

export function ResultsPage() {
  const navigate = useNavigate();
  const { state, rematch, resetToSetup } = useGame();

  const { gameMode } = state;
  const isRaceMode = gameMode === 'speedRun';
  const isGpaBased = gameMode === 'honorRoll' || gameMode === 'pureClassroom';

  /* Rank players based on game mode:
   *   Race = finish order (first to finish wins)
   *   Honor Roll / Pure Classroom = GPA ranking */
  const rankedPlayers = isRaceMode
    ? getPlayersByFinishOrder(state.players)
    : getPlayersByGPA(state.players);

  /* Compute fair final GPAs that account for incomplete classes in Race mode.
   * This prevents GPA inflation when a player hasn't finished all their classes. */
  const finalGpaByPlayerId: Record<string, number> = {};
  for (const player of state.players) {
    finalGpaByPlayerId[player.id] = isRaceMode
      ? calculateFinalGPA(player.classProgress)
      : player.gpa;
  }

  const { winner, subtitle } = getWinnerDisplay(state.players, gameMode);

  function handleRematch() {
    rematch();
    navigate('/play');
  }

  function handleNewGame() {
    resetToSetup();
    navigate('/setup');
  }

  function handleHome() {
    resetToSetup();
    navigate('/');
  }

  return (
    <div className="chalkboard-bg" style={styles.page}>
      {/* Winner announcement */}
      <div style={styles.winnerSection}>
        <span style={styles.trophy}>{'\u{1F3C6}'}</span>
        <h1 className="chalk-text-gold" style={styles.winnerName}>
          {winner ? `${winner.name} Wins!` : 'Game Over!'}
        </h1>
        <p className="chalk-text" style={styles.winnerSubtext}>{subtitle}</p>
      </div>

      {/* Mode badge */}
      <div style={styles.modeBadge}>
        <span style={styles.modeBadgeText}>
          {gameMode === 'pureClassroom' ? 'Pure Classroom' : isGpaBased ? 'Honor Roll' : 'Race'}
        </span>
      </div>

      {/* Report cards for each player */}
      <div style={styles.reportCards}>
        <h2 style={styles.sectionTitle}>Report Cards</h2>
        {rankedPlayers.map((player, rank) => {
          /* GPA-based modes show Valedictorian/Salutatorian; Race mode does not */
          const rankingLabel = isGpaBased
            ? getHonorRollRankingLabel(rank, state.players.length)
            : undefined;

          const displayGpa = finalGpaByPlayerId[player.id] ?? player.gpa;

          return (
            <div key={player.id} style={styles.reportCardWrapper}>
              {/* Rank indicator */}
              <div style={styles.rankBadge}>
                <span style={styles.rankNumber}>#{rank + 1}</span>
                {isGpaBased && (
                  <span style={styles.rankGpa}>
                    {displayGpa.toFixed(2)} GPA
                  </span>
                )}
                {isRaceMode && (
                  <span style={styles.rankScore}>
                    {player.finished ? 'Finished' : `Period ${player.currentPeriod}`}
                  </span>
                )}
              </div>
              <ReportCard
                player={player}
                rankingLabel={rankingLabel}
                overrideGpa={isRaceMode ? displayGpa : undefined}
              />
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        <button className="btn-gold" style={styles.rematchButton} onClick={handleRematch}>
          Rematch!
        </button>
        <button style={styles.newGameButton} onClick={handleNewGame}>
          New Game
        </button>
        <button style={styles.homeButton} onClick={handleHome}>
          Home
        </button>
      </div>
    </div>
  );
}

// _______________________________________________
//              STYLES
// _______________________________________________

const styles: Record<string, React.CSSProperties> = {
  page: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1.5rem 1rem',
    minHeight: '100vh',
    overflowY: 'auto',
  },

  /* Winner section */
  winnerSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    paddingTop: '0.5rem',
  },
  trophy: {
    fontSize: '3rem',
  },
  winnerName: {
    fontSize: '2rem',
    fontWeight: '400',
    textAlign: 'center' as const,
  },
  winnerSubtext: {
    fontSize: '1.1rem',
    fontFamily: 'var(--font-family-handwriting)',
  },

  /* Mode badge */
  modeBadge: {
    display: 'flex',
    justifyContent: 'center',
  },
  modeBadgeText: {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: 'var(--accent-gold)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    padding: '0.25rem 0.75rem',
    border: '1px solid var(--accent-gold)',
    borderRadius: '20px',
    opacity: 0.8,
  },

  /* Report cards section */
  reportCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  reportCardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  rankBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    paddingLeft: '0.25rem',
  },
  rankNumber: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: 'var(--muted)',
  },
  rankGpa: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'var(--accent-gold)',
  },
  rankScore: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'var(--accent-gold)',
  },

  /* Actions */
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: 'auto',
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
  rematchButton: {
    padding: '1rem',
    borderRadius: '10px',
    backgroundColor: 'var(--accent-gold)',
    color: 'var(--text-dark)',
    fontSize: '1.1rem',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
  },
  newGameButton: {
    padding: '0.875rem',
    borderRadius: '10px',
    backgroundColor: 'var(--accent-wood)',
    color: 'var(--text-light)',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
  },
  homeButton: {
    padding: '0.75rem',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    color: 'var(--muted)',
    fontSize: '0.9rem',
    fontWeight: '500',
    border: '1px solid var(--muted)',
    cursor: 'pointer',
  },
};
